export type ChatRole = "user" | "assistant" | "system";

export interface ChatMessage {
  kind: "message";
  id: string;
  role: ChatRole;
  content: string;
  // When the message entered the transcript: stamped client-side for a live turn,
  // hydrated from the persisted `created` for a loaded conversation. Optional so a
  // legacy/in-flight item without a stamp still renders.
  createdAt?: Date;
}

export interface ChatError {
  kind: "error";
  id: string;
  content: string;
  // The user turn this error belongs to, so its Retry re-sends the right text
  // even when a later turn has since moved lastUserContentRef on.
  userContent?: string;
  // The failure code (backend error event, or an HTTP/network mapping). Surfaced
  // subtly for support; drives the actionable copy in `content`.
  code?: string;
  // Backend log-correlation id (#20.D) from the terminal error frame, shown as a
  // copyable id so support can pivot a failed session to its log line.
  requestId?: string | null;
}

export type ChatTranscriptItem = ChatMessage | ChatError;

export interface ReferencedFile {
  resourceId: string;
  name: string;
  fileRevisionId?: string;
  origin: ReferencedFileOrigin;
}

export type ReferencedFileOrigin = "attachment" | "chat-save";

// The upload display name is user-editable and may already carry the extension,
// so only append it when it isn't already there (case-insensitive) to avoid
// duplicated suffixes like "report.pdf.pdf".
export function buildReferencedFileName(displayName: string, extension: string): string {
  if (!extension) return displayName;
  const suffix = `.${extension}`;
  return displayName.toLowerCase().endsWith(suffix.toLowerCase()) ? displayName : `${displayName}${suffix}`;
}
